import {
  createSlice,
  createEntityAdapter,
  createSelector,
  EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";

import {Asset} from "../../../api/assets";
import { createAsset, fetchAssets, updateAsset } from "./assetsThunk";

export type RequestStatus = "idle"| "loading"| "success"| "failed";

export const assetsAdapter = createEntityAdapter<Asset, string>({
    selectId: (asset) =>asset.asset_id,
  });

export interface AssetsState {
  assets: EntityState<Asset,string>;

  status: RequestStatus;

  error?: string | null;

  caseId?: string;
}

const initialState: AssetsState =
  {
    assets:assetsAdapter.getInitialState(),
    status: "idle",
    error: null,
    caseId: undefined,
  };

const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchAssets.pending,(state) => {
          state.status = "loading";
          state.error = null;
          assetsAdapter.removeAll(state.assets);
        },
      )

      .addCase(fetchAssets.fulfilled,(state, action) => {
          state.status = "success";
          state.caseId = action.meta.arg;

          assetsAdapter.setAll(
            state.assets,
            action.payload.assets,
          );
        },
      )

      .addCase(fetchAssets.rejected,(state, action) => {
          state.status = "failed";

          state.error =
            action.payload ??
            "Failed to fetch assets";
        },
      )

      .addCase(createAsset.fulfilled,(state, action) => {
          assetsAdapter.addOne(
            state.assets,
            action.payload,
          );
        },
      )

      .addCase(updateAsset.fulfilled,(state, action) => {
          assetsAdapter.updateOne(
            state.assets,
            {
              id: action.meta.arg.asset_id,
              changes:action.meta.arg.request,
            },
          );
        },
      );
  },
});

export const assetsReducer =assetsSlice.reducer;

const assetsSelectors = assetsAdapter.getSelectors<RootState>((state) => state.assets.assets);

export const {
  selectAll: selectAllAssets,
  selectById: selectAssetById,
  selectEntities: selectAssetEntities,
  selectIds: selectAssetIds,
  selectTotal: selectAssetCount,
} = assetsSelectors;


export const selectAssetsStatus = (state: RootState) => state.assets.status;

export const selectAssetsError = (state: RootState) => state.assets.error;

export const selectAssetsGroupedByClass =createSelector(
    [selectAllAssets],
    assets =>
      assets.reduce<Record<string, Asset[]>>(
        (grouped, asset) => {
          const assetClass =asset.asset_class;

          if (grouped[assetClass] === null || grouped[assetClass] === undefined) {
            grouped[assetClass] = [];
          }

          grouped[assetClass].push(asset);

          return grouped;
        },
        {},
      ),
  );